import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  BarChart3,
  TrendingUp,
  TrendingDown,
  Building2,
  MapPin,
  Briefcase,
  Users,
  Search,
  Filter,
  ArrowUp,
  ArrowDown,
  Minus,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOGO_REGIONS = ["Maritime", "Plateaux", "Centrale", "Kara", "Savanes", "DAGL"];

const SECTORS = [
  "Agriculture",
  "Agroalimentaire",
  "Artisanat",
  "Commerce",
  "Construction/BTP",
  "Éducation/Formation",
  "Industrie",
  "Informatique/Tech",
  "Santé",
  "Services",
  "Tourisme/Hôtellerie",
  "Transport/Logistique",
  "Autre"
];

const GENDERS = [
  { value: "M", label: "Homme" },
  { value: "F", label: "Femme" },
  { value: "Autre", label: "Autre" }
];

export const DigitalizationStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filters
  const [filterRegion, setFilterRegion] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterGender, setFilterGender] = useState('');

  useEffect(() => {
    fetchData();
  }, [filterRegion, filterSector, filterGender]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filterRegion) params.append('region', filterRegion);
      if (filterSector) params.append('sector', filterSector);
      if (filterGender) params.append('gender', filterGender);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [statsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/stats/digitalization${queryString}`),
        axios.get(`${API}/stats/digitalization/companies${queryString}`)
      ]);
      
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilterRegion('');
    setFilterSector('');
    setFilterGender('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getProgressIcon = (progress) => {
    if (progress === null || progress === undefined) return <Minus className="w-4 h-4 text-slate-400" />;
    if (progress > 0) return <ArrowUp className="w-4 h-4 text-emerald-600" />;
    if (progress < 0) return <ArrowDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getProgressBadge = (progress) => {
    if (progress === null || progress === undefined) return null;
    const className = progress > 0 
      ? "bg-emerald-100 text-emerald-700" 
      : progress < 0 
        ? "bg-red-100 text-red-700" 
        : "bg-slate-100 text-slate-700";
    return (
      <Badge className={className}>
        {progress > 0 ? '+' : ''}{progress.toFixed(1)}
      </Badge>
    );
  };

  const filteredCompanies = companies.filter(company => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      company.name?.toLowerCase().includes(search) ||
      company.contact_person?.toLowerCase().includes(search) ||
      company.region?.toLowerCase().includes(search) ||
      company.sector?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Indice de Digitalisation
            </h1>
            <p className="text-slate-500 mt-1">
              Suivi de la transformation digitale des PME
            </p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-slate-500 mb-1 block">Région</label>
                <Select value={filterRegion || "all"} onValueChange={(v) => setFilterRegion(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-region">
                    <SelectValue placeholder="Toutes les régions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les régions</SelectItem>
                    {TOGO_REGIONS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-slate-500 mb-1 block">Secteur</label>
                <Select value={filterSector || "all"} onValueChange={(v) => setFilterSector(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-sector">
                    <SelectValue placeholder="Tous les secteurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {SECTORS.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-slate-500 mb-1 block">Genre du dirigeant</label>
                <Select value={filterGender || "all"} onValueChange={(v) => setFilterGender(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-gender">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {GENDERS.map(g => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(filterRegion || filterSector || filterGender) && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <Filter className="w-4 h-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_companies || 0}</p>
                  <p className="text-sm text-slate-500">PME au total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats?.average_initial !== null ? stats.average_initial : '-'}
                  </p>
                  <p className="text-sm text-slate-500">Indice initial moyen</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {stats?.companies_with_initial || 0} PME renseignées
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats?.average_final !== null ? stats.average_final : '-'}
                  </p>
                  <p className="text-sm text-slate-500">Indice final moyen</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {stats?.companies_with_final || 0} PME renseignées
              </p>
            </CardContent>
          </Card>
          <Card className={stats?.average_progress > 0 ? "border-emerald-200 bg-emerald-50" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stats?.average_progress > 0 ? 'bg-emerald-200' : 'bg-slate-100'}`}>
                  <TrendingUp className={`w-5 h-5 ${stats?.average_progress > 0 ? 'text-emerald-700' : 'text-slate-500'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${stats?.average_progress > 0 ? 'text-emerald-700' : 'text-slate-900'}`}>
                    {stats?.average_progress !== null ? (stats.average_progress > 0 ? '+' : '') + stats.average_progress : '-'}
                  </p>
                  <p className="text-sm text-slate-500">Progression moyenne</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {stats?.companies_with_both || 0} PME avec les deux indices
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="by-region" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="by-region" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Par Région
            </TabsTrigger>
            <TabsTrigger value="by-sector" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Par Secteur
            </TabsTrigger>
            <TabsTrigger value="by-gender" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Par Genre
            </TabsTrigger>
            <TabsTrigger value="companies" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Détail PME
            </TabsTrigger>
          </TabsList>

          {/* By Region */}
          <TabsContent value="by-region">
            <Card>
              <CardHeader>
                <CardTitle>Statistiques par Région</CardTitle>
                <CardDescription>
                  Indices de digitalisation moyens par région du Togo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Région</TableHead>
                      <TableHead className="text-center">PME</TableHead>
                      <TableHead className="text-center">Indice Initial</TableHead>
                      <TableHead className="text-center">Indice Final</TableHead>
                      <TableHead className="text-center">Progression</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.by_region?.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{item.total}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.avg_initial !== null ? (
                            <span className="font-medium">{item.avg_initial.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          <span className="text-xs text-slate-400 ml-1">({item.with_initial})</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.avg_final !== null ? (
                            <span className="font-medium">{item.avg_final.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          <span className="text-xs text-slate-400 ml-1">({item.with_final})</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getProgressIcon(item.avg_progress)}
                            {getProgressBadge(item.avg_progress)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!stats?.by_region || stats.by_region.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          Aucune donnée disponible
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* By Sector */}
          <TabsContent value="by-sector">
            <Card>
              <CardHeader>
                <CardTitle>Statistiques par Secteur</CardTitle>
                <CardDescription>
                  Indices de digitalisation moyens par secteur d&apos;activité
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Secteur</TableHead>
                      <TableHead className="text-center">PME</TableHead>
                      <TableHead className="text-center">Indice Initial</TableHead>
                      <TableHead className="text-center">Indice Final</TableHead>
                      <TableHead className="text-center">Progression</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.by_sector?.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{item.total}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.avg_initial !== null ? (
                            <span className="font-medium">{item.avg_initial.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          <span className="text-xs text-slate-400 ml-1">({item.with_initial})</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.avg_final !== null ? (
                            <span className="font-medium">{item.avg_final.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          <span className="text-xs text-slate-400 ml-1">({item.with_final})</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getProgressIcon(item.avg_progress)}
                            {getProgressBadge(item.avg_progress)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!stats?.by_sector || stats.by_sector.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          Aucune donnée disponible
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* By Gender */}
          <TabsContent value="by-gender">
            <Card>
              <CardHeader>
                <CardTitle>Statistiques par Genre</CardTitle>
                <CardDescription>
                  Indices de digitalisation moyens selon le genre du dirigeant
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Genre</TableHead>
                      <TableHead className="text-center">PME</TableHead>
                      <TableHead className="text-center">Indice Initial</TableHead>
                      <TableHead className="text-center">Indice Final</TableHead>
                      <TableHead className="text-center">Progression</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.by_gender?.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">
                          {item.name === 'M' ? 'Homme' : item.name === 'F' ? 'Femme' : item.name}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{item.total}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.avg_initial !== null ? (
                            <span className="font-medium">{item.avg_initial.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          <span className="text-xs text-slate-400 ml-1">({item.with_initial})</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.avg_final !== null ? (
                            <span className="font-medium">{item.avg_final.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                          <span className="text-xs text-slate-400 ml-1">({item.with_final})</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getProgressIcon(item.avg_progress)}
                            {getProgressBadge(item.avg_progress)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!stats?.by_gender || stats.by_gender.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          Aucune donnée disponible
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Companies Detail */}
          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <CardTitle>Détail par PME</CardTitle>
                <CardDescription>
                  Liste des entreprises avec leurs indices de digitalisation
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Rechercher par nom, contact, région..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-companies"
                  />
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entreprise</TableHead>
                        <TableHead>Région</TableHead>
                        <TableHead>Secteur</TableHead>
                        <TableHead>Dirigeant</TableHead>
                        <TableHead className="text-center">Initial</TableHead>
                        <TableHead className="text-center">Final</TableHead>
                        <TableHead className="text-center">Progression</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCompanies.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell className="font-medium">{company.name}</TableCell>
                          <TableCell>
                            {company.region ? (
                              <Badge variant="outline" className="text-blue-600 border-blue-200">
                                {company.region}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">{company.sector || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{company.contact_person || '-'}</span>
                              {company.contact_gender && (
                                <Badge variant="secondary" className="text-xs">
                                  {company.contact_gender === 'M' ? 'H' : company.contact_gender === 'F' ? 'F' : company.contact_gender}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {company.initial_digitalization_index !== null ? (
                              <div>
                                <span className="font-medium text-amber-600">{company.initial_digitalization_index}</span>
                                <p className="text-xs text-slate-400">{formatDate(company.initial_index_date)}</p>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {company.final_digitalization_index !== null ? (
                              <div>
                                <span className="font-medium text-emerald-600">{company.final_digitalization_index}</span>
                                <p className="text-xs text-slate-400">{formatDate(company.final_index_date)}</p>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {getProgressIcon(company.progress)}
                              {getProgressBadge(company.progress)}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredCompanies.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                            Aucune entreprise trouvée
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
